import { giphyIndigo, giphyLightCharcoal, giphyWhite } from '@giphy/js-brand'
import dateFormat from 'dateformat'
import { at, filter, range, uniq } from 'lodash'
import React, { MouseEvent, SyntheticEvent, useContext, useEffect, useState } from 'react'
import MessageContext from 'shared/components/context/message'
import { googleAnalyticsEvent, trackPageView } from 'shared/util/analytics'
import styled, { css } from 'styled-components'
import { CreationToolsContext } from '../../../manager'
import CreationToolOutputType from '../../../types/output-type'
import { PillBlock } from '../../blocks'
import Footer from '../shared/components/footer'
import Modal from '../shared/components/modal'
import SupportCTA from '../shared/components/support-cta'
import SelectAll from '../shared/icons/select-all'
import UnselectAll from '../shared/icons/unselect-all'
import { ICapture, ISegment } from '../shared/types'
import API from '../shared/utils/api'
import Uploader from '../shared/utils/uploader'
import { getDefaultEditParams } from '../shared/utils/utils'
import UploadButtons from './buttons'
import Editor from './editor'
import Grid from './grid'
import Header from './header'
import Merge from './merge'
import EditCaptureModal from '../modals/edit-capture'

const api = new API()

const BottomBar = styled.div`
    left: 0;
    bottom: 0;
    width: 100%;
    margin: 50px 0px;
`

const TopBar = styled.div`
    padding: 30px 0;
    position: sticky;
    top: 0;
    background-color: black;
    z-index: 1;
`

const TabLabel = styled.div<{ active: boolean }>`
    cursor: pointer;
    color: ${giphyLightCharcoal};
    font-size: 17px;
    font-weight: bold;
    display: inline;
    margin-right: 20px;

    ${props =>
        props.active &&
        css`
            color: ${giphyWhite};
        `}
`

const Actions = styled.div`
    float: right;
    font-size: 14px;
    color: ${giphyLightCharcoal};
    font-weight: bold;
`

const Action = styled.span`
    margin: 0 10px;
    cursor: pointer;
`

const ActionIcon = styled.span`
    padding-right: 7px;
    vertical-align: middle;
    color: ${giphyWhite};
`

const Pill = styled(PillBlock)`
    background: ${giphyIndigo};
    margin-left: 5px;
`

const Detail = ({ match }: any) => {
    const { setFiles } = useContext(CreationToolsContext)
    const [capture, setCapture] = useState<ICapture>()
    const [originalSegments, setOriginalSegments] = useState<ISegment[]>([])
    const [editedSegments, setEditedSegments] = useState<ISegment[]>([])
    const [selected, setSelected] = useState<number[]>([])
    const [editing, setEditing] = useState<number>()
    const [merging, setMerging] = useState<number[]>()
    const [activeTab, setActiveTab] = useState('all')
    const [segments, setSegments] = useState<ISegment[]>([])
    const [soundEnabled, setSoundEnabled] = useState(false)
    const [lastSelected, setLastSelected] = useState<number>()
    const { sendMessage } = useContext(MessageContext)
    const [showEditCaptureModal, setShowEditCaptureModal] = useState(false)

    useEffect(() => {
        const captureId = match.params.captureId
        trackPageView(`/supercut/detail/${captureId}`)

        const fetchCaptureDetail = async () => {
            const data: { capture: ICapture; segments: ISegment[] } = await api.getCaptureDetails(captureId)
            setCapture(data.capture)
            setOriginalSegments(data.segments)
        }

        const fetchUserEdits = async () => {
            const data = await api.getUserEdits(captureId)
            setEditedSegments(data)
        }

        fetchCaptureDetail()
        fetchUserEdits()
    }, [])

    useEffect(() => {
        refreshSegmentsData()
    }, [originalSegments, editedSegments])

    const saveUserEdit = (segment: ISegment) => {
        let update = [...editedSegments]
        update[segment.index] = segment
        setEditedSegments(update)
        api.saveUserEdits(capture!.id, update)
    }

    const editClosed = () => {
        setEditing(undefined)
        refreshSegmentsData()
    }

    const soundClicked = () => {
        googleAnalyticsEvent({
            category: 'Super Cut',
            action: 'Sound Toggle',
            label: `${soundEnabled ? 'On' : 'Off'}`,
        })
        setSoundEnabled(!soundEnabled)
    }

    const uploadClicked = async (isClips: boolean) => {
        const outputType = isClips ? CreationToolOutputType.Video : CreationToolOutputType.Gif

        googleAnalyticsEvent({
            category: 'Super Cut',
            action: isClips ? 'Upload Clips' : 'Upload GIFs',
            label: capture!.id,
            value: selected.length,
        })

        const selectedURLs = await Uploader.getVideoURLs(
            capture!,
            selected.map(i => segments[i])
        )
        const sources = await Uploader.processURLs(selectedURLs, capture!)

        setFiles(sources, outputType)
    }

    const selectAllClicked = () => {
        if (selected.length !== segments.length) {
            googleAnalyticsEvent({
                category: 'Super Cut',
                action: 'Select All',
                label: capture!.id,
            })
        }
        setSelected(range(segments.length))
    }

    const unselectAllClicked = () => {
        if (selected.length > 0) {
            googleAnalyticsEvent({
                category: 'Super Cut',
                action: 'Unselect All',
                label: capture!.id,
            })
        }
        setSelected([])
    }

    const mergeClicked = () => {
        setMerging(selected)
    }

    const mergeClosed = () => {
        setMerging(undefined)
        refreshSegmentsData()
    }

    const settingsClicked = async () => {
        setShowEditCaptureModal(true)
    }

    const closeEditModal = () => {
        setShowEditCaptureModal(false)
    }

    const refreshSegmentsData = () => {
        let segments = [] as ISegment[]

        if (capture && originalSegments) {
            // Merge the original segments from this capture with any edited/custom segments.
            for (let i = 0; i < Math.max(originalSegments.length, editedSegments.length); i++) {
                if (editedSegments[i]) {
                    segments[i] = editedSegments[i]
                } else if (originalSegments[i]) {
                    segments[i] = originalSegments[i]
                }
            }
        }

        setSegments(segments)
    }

    if (!capture) {
        return null
    } else {
        return (
            <div>
                {showEditCaptureModal && (
                    <EditCaptureModal
                        capture={capture}
                        updateCapture={setCapture}
                        preview={segments[0].video_url}
                        onClose={closeEditModal}
                    />
                )}
                {capture && (
                    <Header
                        title={capture!.title}
                        subtitle={dateFormat(new Date(capture!.date), 'mmm dS, yyyy h:MM TT')}
                    />
                )}

                {editing !== undefined && (
                    <Modal onClickOutside={editClosed}>
                        <Editor
                            capture={capture!}
                            segment={segments[editing!]}
                            onCancel={editClosed}
                            onSave={(segment: ISegment) => {
                                saveUserEdit(segment)
                                editClosed()
                            }}
                            soundEnabled={soundEnabled}
                        />
                    </Modal>
                )}

                {merging !== undefined && (
                    <Modal onClickOutside={mergeClosed}>
                        <Merge
                            capture={capture!}
                            segments={segments}
                            onCancel={mergeClosed}
                            onSave={(segment: ISegment) => {
                                saveUserEdit(segment)
                                setSelected([...selected, segment.index])
                                mergeClosed()
                            }}
                            soundEnabled={soundEnabled}
                            initialSelection={merging}
                        />
                    </Modal>
                )}

                <TopBar>
                    <TabLabel onClick={() => setActiveTab('all')} active={activeTab === 'all'}>
                        All Scenes <Pill>{segments.length}</Pill>
                    </TabLabel>
                    <TabLabel onClick={() => setActiveTab('selected')} active={activeTab === 'selected'}>
                        Selected Scenes <Pill>{selected.length}</Pill>
                    </TabLabel>

                    <Actions>
                        <Action onClick={selectAllClicked}>
                            <SelectAll /> Select All
                        </Action>
                        <Action onClick={unselectAllClicked}>
                            <UnselectAll /> Unselect All
                        </Action>
                        <Action onClick={soundClicked}>
                            <ActionIcon className={soundEnabled ? 'ss-highvolume' : 'ss-volume'} />
                            Sound
                        </Action>
                        <Action onClick={mergeClicked}>
                            <ActionIcon className="ss-merge" />
                            Merge Clips
                        </Action>
                        <Action onClick={settingsClicked}>
                            <ActionIcon className="ss-settings" />
                            Edit Capture
                        </Action>
                    </Actions>
                </TopBar>

                <Grid
                    capture={capture!}
                    segments={activeTab === 'all' ? segments : filter(at(segments, selected), s => !!s)}
                    selected={selected}
                    segmentClicked={(e: SyntheticEvent<HTMLElement, Event>, index: number) => {
                        e.preventDefault()
                        const mouseEvent = (e as unknown) as MouseEvent
                        let newlySelected = [...selected]

                        const foundIndex = selected.indexOf(index)

                        if (mouseEvent.shiftKey) {
                            const startIndex = lastSelected || 0
                            const endIndex = index

                            newlySelected = uniq([
                                ...selected,
                                ...range(Math.min(startIndex, endIndex), Math.max(startIndex, endIndex) + 1),
                            ])
                        } else {
                            // if it's not selected, add it, otherwise remove it from the selected list.
                            if (foundIndex === -1) {
                                newlySelected.push(index)
                            } else {
                                newlySelected.splice(foundIndex, 1)
                            }
                        }

                        setSelected(newlySelected)
                        setLastSelected(index)
                    }}
                    editClicked={(index: number) => {
                        googleAnalyticsEvent({
                            category: 'Super Cut',
                            action: 'Edit-Click',
                            label: capture.id,
                        })
                        setEditing(index)
                    }}
                    mergeClicked={(index: number) => {
                        setMerging([index])
                    }}
                    deleteClicked={(index: number) => {
                        const segment = segments[index]
                        saveUserEdit({
                            ...segment,
                            editParams: {
                                ...segment.editParams,
                                displayHidden: true,
                            },
                        } as ISegment)
                        googleAnalyticsEvent({
                            category: 'Super Cut',
                            action: 'Delete',
                            label: capture.id,
                        })
                        sendMessage('GIF Deleted!')
                    }}
                    duplicateClicked={(index: number) => {
                        const segment = segments[index]
                        saveUserEdit({
                            ...segment,
                            index: segments.length,
                            editParams: {
                                ...segment.editParams,
                                displayOrder: index,
                            },
                        } as ISegment)
                        googleAnalyticsEvent({
                            category: 'Super Cut',
                            action: 'Duplicate',
                            label: capture.id,
                        })
                        sendMessage('GIF Duplicated!')
                    }}
                    revertClicked={(index: number) => {
                        const segment = segments[index]
                        saveUserEdit({
                            ...segment,
                            editParams: getDefaultEditParams(capture, segment),
                        } as ISegment)
                        sendMessage('GIF Reverted!')
                    }}
                    soundEnabled={soundEnabled}
                />
                {selected.length > 0 && <UploadButtons uploadCallback={uploadClicked} />}
                <SupportCTA />
                <BottomBar>
                    <Footer />
                </BottomBar>
            </div>
        )
    }
}

export default Detail
